/**
 * SubPivot — secondary segmented chip strip beneath a parent tab.
 *
 * Part of the Cockpit Page Standard (see src/components/cockpit/README.md).
 *
 * Anatomy:
 *   • Container — `bg-muted/30` track with hairline border so the chips
 *     read as a single segmented control (not floating buttons).
 *   • Active chip — primary-tinted fill + ring + colored icon AND label,
 *     same hue and weight, so the toggled state reads as one unit.
 *
 * Motion:
 *   • Reveal cascade (50ms stagger) plays only when `revealKey` changes —
 *     i.e. when the parent tab is clicked and the sub-pivot first appears.
 *   • Switching between chips inside the same tab does NOT re-animate;
 *     the color transition alone is enough feedback. (Avoids flicker.)
 */
import * as React from "react";
import { cn } from "@/lib/utils";

export interface SubPivotOption<T extends string> {
  id: T;
  label: string;
  /** Tooltip text shown on hover (also used for screen-readers via title). */
  sub?: string;
  icon?: React.ReactNode;
}

interface Props<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: SubPivotOption<T>[];
  /** Bump this when the parent context changes to replay the entrance cascade. */
  revealKey?: string | number;
  ariaLabel?: string;
  className?: string;
}

export function SubPivot<T extends string>({
  value,
  onChange,
  options,
  revealKey,
  ariaLabel = "Sub-pivot",
  className,
}: Props<T>) {
  return (
    <div key={revealKey} className={cn("mb-5", className)}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="inline-flex items-center gap-0.5 rounded-md border border-border/60 bg-muted/30 p-0.5"
      >
        {options.map((opt, i) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(opt.id)}
              title={opt.sub}
              style={{
                animation: "kdr-subpivot-reveal 0.32s cubic-bezier(0.22, 1, 0.36, 1) both",
                animationDelay: `${i * 50}ms`,
              }}
              className={cn(
                "relative flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] transition-colors",
                active
                  ? "bg-primary/15 text-primary shadow-sm ring-1 ring-primary/30 font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium",
              )}
            >
              {opt.icon && (
                <span className={cn("opacity-70", active && "opacity-100 text-primary")}>
                  {opt.icon}
                </span>
              )}
              <span className={cn(active && "text-primary tracking-tight")}>{opt.label}</span>
            </button>
          );
        })}
      </div>
      <style>{`
        @keyframes kdr-subpivot-reveal {
          0%   { opacity: 0; transform: translateY(-6px) translateX(-8px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default SubPivot;
